/**
 * This class provides a top-level API to manage payment processor functionality.
 *
 * Each new payment processor will provide a secondary file that this one will delegate to.
 *
 * The payment processor used will be determined automatically:  this does not need to be set anywhere in this file.
 */

import LawPay from './LawPay/LawPay';
import Payrix from './Payrix/Payrix';
import store from '../../store/store';
import util from '../../lib/util';
import {contactPaymentFacts, type PaymentEligibilityInfo} from './paymentFacts';
import OptionHelper from '../../Configuration/GeneralOptions/OptionHelper';
import pWaitFor from 'p-wait-for';
import {getCurrentFirm, getCurrentUserId, isPortalUser} from "../../lib/picos/currentLogin";
import Icons, {IconType} from "../../ui/IconButton/Icons";
import Contact from "../../entities/contact";
import {PayrixCustomer, PayrixPaymentMethod, PayrixTransaction, PayrixTransactionFull} from "../../@types/payrix";
import {LawpayContact, LawpayPaymentMethod, LawpayTransaction} from "../../@types/lawpay";
import {uuid} from "../../@types/entity";
import {PaymentMethod, ProcessorPaymentMeta, StandardAccount} from "../../@types/payments";
import Address from "../../entities/address";
import Transaction from "../../entities/transaction";
import Lead from "../../entities/lead";
import Matter from "../../entities/matter";
import {Result} from "oxide.ts";
import {globalAbortSignal} from "../../lib/picos/abortController";

export const LAWPAY_READ_ONLY = 'LawPay is closed and read-only. Use Outlaw Payments.';

export enum ProcessorName {
    Payrix = 'payrix',
    LawPay = 'lawpay'
}

export type {PaymentEligibilityInfo};

export type TransactionFlag = 'CHARGE' | 'REFUND' | 'INCOMPLETE' | 'COMPLETE' | 'VOID' | 'REFUNDED';

export default {
    /* -------------  Helper Functions  -------------*/

    /**
     * Check if a payments processor is set up. Return true if one is set up.
     *
     * @returns {boolean}
     */
    usesPayments(): boolean {
        return getCurrentFirm()?.paymentProcessor !== null;
    },

    /**
     * Get the current payment processor, or null if none selected.
     *
     * @returns {ProcessorName}
     */
    getPaymentProcessor(): 'lawpay' | 'payrix' {
        return getCurrentFirm().paymentProcessor as ProcessorName;
    },

    isLawpay() {
        return this.getPaymentProcessor() === ProcessorName.LawPay;
    },

    isPayrix() {
        return this.getPaymentProcessor() === ProcessorName.Payrix;
    },

    /**
     * Get the formal name of this processor.
     *
     * @param {ProcessorName} [paymentProcessor]
     * @returns {string}
     */
    getName(paymentProcessor?: ProcessorName): string {
        return this.getDelegate(paymentProcessor)?.getName();
    },

    /**
     * Get the icon representing this processor.
     *
     * @param {ProcessorName} [paymentProcessor]
     * @returns {ReactElement}
     */
    getIcon(paymentProcessor: ProcessorName = ProcessorName.Payrix): string {
        if (paymentProcessor === 'payrix') return Payrix.getIcon();
        else if (paymentProcessor === 'lawpay') return LawPay.getIcon();
        else return null;
    },

    /**
     * Are we connected to (a specific) payment processor?  Similar to usesPayments, but specific to a type of
     * payment processor.
     *
     * @param {ProcessorName} [paymentProcessor]
     * @return {boolean}
     */
    isConnected(paymentProcessor?: ProcessorName): boolean {
        if (!paymentProcessor) return this.usesPayments();
        else return this.getPaymentProcessor() === paymentProcessor;
    },

    /**
     * Get the proper delegate based on the payment processor provided, or the currently set up payment processor.
     *
     * @param {ProcessorName} [paymentProcessor]
     */
    getDelegate(paymentProcessor?: ProcessorName) {
        // if (!this.usesPayments()) return null;

        const processorName = paymentProcessor || this.getPaymentProcessor();

        // Check for a valid payment processor
        if (!processorName) return null;

        // Get the right delegate
        switch (processorName) {
            case 'payrix':
                return Payrix;
            case 'lawpay':
                return LawPay;
            default:
                throw Error(`No payment processor delegate for ${processorName}`);
        }
    },

    /* -------------  Contact Functions  -------------*/

    /**
     * Get a complete list of contacts from the processor.
     *
     * The return type is dependent on the processor (for now). We should transpose into a standardized form.
     *
     * @returns {Promise<PayrixCustomer[]|LawpayContact[]>}
     */
    async getContacts(): Promise<PayrixCustomer[] | LawpayContact[]> {
        return this.getDelegate()?.getContacts();
    },

    /**
     * Sync contacts with the processor.
     *
     * @param {Contact[]} [contacts] - optional list of contacts to sync
     * @returns {Promise<string>}
     */
    async syncContacts(contacts: Contact[]): Promise<string> {
        return this.getDelegate()?.syncContacts(contacts)
    },

    /**
     * Delete a specific contact on the processor.
     *
     * @param {uuid} processorContactId
     * @returns {Promise<void>}
     */
    async deleteContact(processorContactId: uuid): Promise<void> {
        await this.getDelegate()?.deleteContact(processorContactId);
    },

    /**
     * Is this contact eligible to have payments?
     *
     * Note that this seems to differ between LawPay and Payrix, so this should be at the processor level.
     *
     * @param {Contact} contact
     * @param {Lead | Matter} unsavedEntity
     * @returns {PaymentEligibilityInfo}
     */
    paymentEligible(contact: Contact, unsavedEntity?: Lead | Matter) {
        if (!this.getDelegate()) return false;
        else return this.getDelegate()?.paymentEligible(contact, unsavedEntity);
    },

    /**
     *
     * @param {Contact} contact
     * @returns {PaymentEligibilityInfo}
     */
    paymentEligibilityInfo(contact: Contact, unsavedEntity?: Lead | Matter): PaymentEligibilityInfo {
        if (!contact || !this.usesPayments()) return {
            isPayer: false,
            hasFullName: false,
            hasEmail: false,
            hasAddress: false,
            hasPhone: false
        };

        return contactPaymentFacts(contact, unsavedEntity);
    },

    /**
     * Update a contact with new information (sync a single contact).
     *
     * @param {Contact} contact
     * @returns {Promise<string>}
     */
    async updateContact(contact: Contact): Promise<PayrixCustomer> {
        return this.getDelegate()?.updateContact(contact);
    },

    async updateTxn(txnId: uuid, description: string): Promise<void> {
        return Payrix.updateTxn(txnId, description);
    },

    /* -------------  Connectivity Functions  -------------*/

    /**
     * Call the signup flow.
     *
     * @param {ProcessorName} paymentProcessor
     * @returns {string} - the redirect_uri
     */
    async signup(paymentProcessor: ProcessorName): Promise<string> {
        if (paymentProcessor === 'lawpay') throw new Error(LAWPAY_READ_ONLY);

        const signupResult = await this.getDelegate(paymentProcessor)?.signup();

        const optionHelper = new OptionHelper();

        // Set the option specifying that we are applying
        optionHelper.setOption(`${paymentProcessor} applicationId`, signupResult.applicationId);

        // Give the program time to actually save it, because the next command is a redirect!
        try {
            await pWaitFor(() => {
                const option = optionHelper.getOptionByLabel(`${paymentProcessor} applicationId`);
                return !!option?.dbAddedOn;
            }, {timeout: 10_000, interval: 500, signal: globalAbortSignal});
        }
        catch (err) {
            // Ignored, just a timeout.
        }

        if (signupResult.redirect_uri) return signupResult.redirect_uri;
        else return '';
    },

    /**
     * Begin the OAuth flow for the given payment processor.
     *
     * @param {ProcessorName} paymentProcessor
     */
    async startConnectionFlow(paymentProcessor: ProcessorName) {
        // Checks
        if (!paymentProcessor) throw Error('No payment processor specified');

        if (!["lawpay", "payrix"].includes(paymentProcessor)) {
            throw Error(`Unsupported payment processor: ${paymentProcessor}`);
        }

        if (paymentProcessor === 'lawpay') throw new Error(LAWPAY_READ_ONLY);

        // Delegate
        return this.getDelegate(paymentProcessor)?.connect();
    },

    /**
     * TODO: Not used right now?
     *
     * @param {ProcessorName} paymentProcessor
     * @returns {boolean}
     */
    applicationInProgress(paymentProcessor: ProcessorName): boolean {
        try {
            const applicationId = new OptionHelper().getOptionValue(`${paymentProcessor} applicationId`);
            return !!applicationId;
        }
        catch (err) {
            return false;
        }
    },

    /**
     * Start the flow to disconnect the payment processor
     * @returns {Promise<void>}
     */
    async startDisconnectionFlow(): Promise<void> {
        // Checks
        if (!this.usesPayments()) throw Error('Not connected to a payment processor');

        if (this.isLawpay()) throw new Error(LAWPAY_READ_ONLY);

        // Delegate
        return this.getDelegate()?.disconnect?.();
    },

    async getAccounts(): Promise<StandardAccount[]> {
        if (!store.getState().misc.isConnected) return null;

        return this.getDelegate()?.getAccounts();
    },


    /* -------------  Payment Methods  -------------*/

    /**
     * Get a payment wallet instance for a contact (this will be a Wallet object).
     *
     * @param {Contact} contact
     */
    async getPaymentMethods(contact: Contact): Promise<PaymentMethod[]> {
        if (!contact) throw new TypeError('contact not provided to getPaymentMethods');

        if (!store.getState().misc.isConnected) return [];

        return this.getDelegate()?.getPaymentMethods(contact);
    },

    /**
     * Get payment methods for multiple contacts.
     *
     * @param {Contact[]} contacts
     * @returns {Promise<PaymentMethod[]>}
     */
    async getPaymentMethodsMulti(contacts: Contact[]): Promise<PaymentMethod[]> {
        if (!contacts) throw new TypeError('contacts not provided to getPaymentMethodsMulti');

        if (!store.getState().misc.isConnected) return [];

        const promises: (Promise<PaymentMethod>[])[] = contacts
            .filter(c => !this.isPayrix() || !isPortalUser() || c.id === getCurrentUserId())
            .map(c => this.getPaymentMethods(c));

        // Wait until they all settle
        const paymentMethods = await Promise.allSettled(promises);

        const result = [];
        for (let i = 0; i < paymentMethods.length; i++) {
            const pms = await promises[i];
            pms.forEach(pm => result.push(pm));
        }

        return result;
    },

    /**
     * Format an Outlaw Address for the payment provider.
     *
     * @param {Address} address
     */
    formatAddress(address: Address) {
        return this.getDelegate()?.formatAddress(address);
    },

    /**
     * Each processor prefers a specific naming convention.
     *
     * @param {PaymentMethod} paymentMethod
     * @returns {PaymentMethod}
     */
    formatPaymentMethod(paymentMethod: PaymentMethod): PaymentMethod {
        return this.getDelegate()?.formatPaymentMethod(paymentMethod);
    },

    /**
     * Add a card or bank account.
     *
     * @param {Contact} contact
     * @param {PaymentMethod} paymentMethod
     * @returns {Promise<void>}
     */
    async addPaymentMethod(contact: Contact, paymentMethod: PaymentMethod): Promise<void> {
        return this.getDelegate()?.createPaymentMethod(contact, paymentMethod);
    },

    /**
     * Delete a payment method from a contact.
     *
     * @param {Contact} contact
     * @param {PaymentMethod} paymentMethod
     * @returns {Promise<void>}
     */
    async deletePaymentMethod(contact: Contact, paymentMethod: PaymentMethod): Promise<void> {
        return this.getDelegate()?.deletePaymentMethod(contact, paymentMethod);
    },

    /* -------------  Making Payments and Related Transactions -------------*/

    /**
     * Create a payment using the given payment method for the given amount.
     *
     * @param {string} paymentMethodId - payment method ID from the provider
     * @param {number} amount - amount of the transaction.  Debits will be positive numbers, credits will be negative!
     * @param {string} accountId - the account in which to create the charge
     * @param {Transaction} transaction - the account in which to create the charge
     * @returns {Promise<void>}
     */
    async createPayment(paymentMethodId: string, amount: number, accountId: string, transaction: Transaction | Record<string, unknown>):  Promise<Result<PayrixTransactionFull, Error>> {
        return this.getDelegate()?.createPayment(paymentMethodId, amount, accountId, transaction);
    },

    /**
     * Refund a transaction.
     *
     * @param {string} transactionId
     * @param {number} amount - amount in $.cc
     * @param {string} refundKey - UUID naming this refund, the same on every retry of it (OWA-1187)
     * @returns {Promise<Transaction>}
     */
    async refundTransaction(transactionId: string, amount: number, refundKey: string): Promise<PayrixTransaction | LawpayTransaction> {
        return this.getDelegate()?.refundTransaction(transactionId, amount, refundKey);
    },

    /**
     * Void a transaction.
     *
     * @param {string} transactionId
     * @param {string} refundKey - UUID naming the refund this void can become, the same on every retry of it
     * @returns {Promise<*>}
     */
    async voidTransaction(transactionId: string, refundKey: string): Promise<PayrixTransaction | LawpayTransaction> {
        return this.getDelegate()?.voidTransaction(transactionId, refundKey);
    },

    /**
     * Get the current status of a transaction.
     *
     * This will return an array of status flags.  Specifically:
     *
     * - NONE: no processor action is associated with this transaction.
     * - INCOMPLETE:  the transaction is in progress, and can be voided.
     * - COMPLETED: the transaction has been settled, and can be refunded but not voided.
     * - VOID: the transaction has been voided.  This is immediate, so once voided nothing can be done.
     * - REFUND: the transaction is being/has been refunded.  Check for INCOMPLETE vs COMPLETED.
     * - CHARGE: the transaction is being/has been processed as a charge.  Check for INCOMPLETE vs COMPLETED.
     *
     * Valid combinations are:
     * - CHARGE/INCOMPLETE
     * - CHARGE/COMPLETE
     * - VOID/COMPLETE
     * - REFUND/INCOMPLETE
     * - REFUND/COMPLETE
     *
     * @param {Transaction} tr
     * @returns {flags: TransactionFlag[], message: string} - an array of flags indicating current status.
     */
    getCurrentStatus(tr: LawpayTransaction | PayrixTransaction): {flags: TransactionFlag[], message: string} {
        // If no payment info yet, the status is NOT_PROCESSED.
        return this.getDelegate()?.getCurrentStatus(tr);
    },

    /**
     * Get the completion date for the transaction.
     *
     * @param {Transaction} tr
     * @returns {Date|null}
     */
    getCompletionDate(tr: Transaction): Date | null {
        return this.getDelegate()?.getCompletionDate(tr.processorPaymentMeta);
    },

    /**
     * Get full details on a transaction.
     *
     * @param {string} transactionId
     * @returns {Promise<PayrixTransaction | LawpayTransaction>}
     */
    async getTransaction(transactionId: string): Promise<PayrixTransaction | LawpayTransaction> {
        return this.getDelegate()?.getTransaction(transactionId);
    },

    /**
     * Get all transactions for a given date range.
     *
     * @param {Date} startDate
     * @param {Date} endDate
     * @returns {Promise<*>}
     */
    async getTransactions(startDate: Date, endDate: Date): Promise<(PayrixTransaction | PayrixTransactionFull | LawpayTransaction)[]> {
        return this.getDelegate()?.getTransactions(startDate, endDate);
    },

    /**
     * Parse out fields we care about from a payment/void/refund, specifically:
     * OUTDATED, see jsdoc
     * - authorization_code: for display to the user
     * - id: the ID of the transaction, for later lookup
     * - status: e.g. VOIDED, AUTHORIZED, PENDING, COMPLETED, ACTIVE
     * - type: e.g. CHARGE, REFUND, CREDIT (note that void returns CHARGE)
     * - amount: the amount charged
     * - amount_refunded: the amount refunded, if a refund
     * - chargeDate: the date the payment method was initially charged
     * - voidOrRefundDate: the date the payment was (last) voided or refunded
     *
     * @param meta the processor transaction to parse
     * @param tr the local Transaction (OWA-595): the Payrix delegate uses it to
     *   rebuild a missing description (id/isTrust/notes) for legacy txns whose
     *   processor description is empty. LawPay ignores it. Must be the Transaction,
     *   NOT a PaymentMethod — the actual payment method comes from the txn itself.
     */
    parsePaymentMeta(meta: PayrixTransaction | PayrixTransactionFull, tr?: Transaction): ProcessorPaymentMeta {
        return this.getDelegate()?.parsePaymentMeta(meta, tr);
    },

    /**
     * Render a bank icon or a credit card icon.
     *
     * @param {PaymentMethod} pm
     * @returns {IconType}
     */
    getPaymentIcon(pm: PaymentMethod): IconType {
        if (pm.type === 'bank') return Icons.BANK;
        else {
            if (this.isPayrix()) {
                const custom = pm as PayrixPaymentMethod;
                // const custom = typeof (paymentMethod.custom) === 'string' ? JSON.parse(paymentMethod.custom) : paymentMethod.custom;
                switch (custom.cardType) {
                    case 'AMEX':
                        return Icons.CC_AMEX;
                    case 'Visa':
                        return Icons.CC_VISA;
                    case 'Mastercard':
                        return Icons.CC_MASTERCARD;
                    case 'Diners Club':
                        return Icons.CC_DINERS_CLUB;
                    case 'Discover':
                        return Icons.CC_DISCOVER;
                    default:
                        return Icons.CREDIT_CARD;
                }
            }
            else {
                const custom = pm  as unknown as LawpayPaymentMethod;
                switch(custom.card_type) {
                    case 'American Express':
                        return Icons.CC_AMEX;
                    case 'Visa':
                        return Icons.CC_VISA;
                    case 'Mastercard':
                        return Icons.CC_MASTERCARD;
                    case 'Diners Club':
                        return Icons.CC_DINERS_CLUB;
                    case 'Discover':
                        return Icons.CC_DISCOVER;
                    default:
                        return Icons.CREDIT_CARD;
                }

                // const test = {
                //     "id": "7322li9mTlConnMCCwfH1Q",
                //     "type": "CHARGE",
                //     "status": "COMPLETED",
                //     "authorization_code": "79968Z",
                //     "amount": 175,
                //     "amount_refunded": 0,
                //     "chargeDate": "2022-06-03T13:33:48.670Z",
                //     "voidOrRefundDate": "2022-06-04T04:02:48.863Z",
                //     "method": {"type": "card", "number": "************9824", "cardType": "Mastercard", "accountType": null, "fingerprint": "_OLem0EmMqPWlvaJ2sjF"}
                // }
            }
        }
    }

}
